import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Text,
  Divider,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  IndexFilters,
  useSetIndexFiltersMode,
  ChoiceList,
  Pagination,
  Select,
  TextField,
  Scrollable,
  Button,
  Icon,
  ProgressBar,
  Badge,
  ActionList,
  Popover,
} from '@shopify/polaris';
import { MenuHorizontalIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { ApiErrorHandler, withApiErrorHandling } from '../utils/apiErrorHandler';

interface ImportJob {
  id: number;
  status: string;
  progress: number;
  total_records: number;
  processed_records: number;
  error_count: number;
  success_count: number;
  created_count: number;
  updated_count: number;
  uploaded_file: string;
  error_file?: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  duration_formatted: string;
  success_rate: number;
  error_rate: number;
}

interface PaginationInfo {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface Filters {
  status?: string[];
}

// Default views configuration
const defaultImportJobViews = [
  {
    id: 'all',
    name: 'All',
    filters: {},
  },
  {
    id: 'pending',
    name: 'Pending',
    filters: { status: 'pending' },
  },
  {
    id: 'processing',
    name: 'Processing',
    filters: { status: 'processing' },
  },
  {
    id: 'completed',
    name: 'Completed',
    filters: { status: 'completed' },
  },
  {
    id: 'failed',
    name: 'Failed',
    filters: { status: 'failed' },
  },
  {
    id: 'cancelled',
    name: 'Cancelled',
    filters: { status: 'cancelled' },
  },
];

// Sort options configuration
const importJobSortOptions = [
  { label: 'Newest first', value: 'created_at desc' as const, directionLabel: 'Newest first' },
  { label: 'Oldest first', value: 'created_at asc' as const, directionLabel: 'Oldest first' },
  { label: 'Status (A-Z)', value: 'status asc' as const, directionLabel: 'Status (A-Z)' },
  { label: 'Status (Z-A)', value: 'status desc' as const, directionLabel: 'Status (Z-A)' },
];

const ImportJobs: React.FC = () => {
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [queryValue, setQueryValue] = useState('');
  const [sortSelected, setSortSelected] = useState(['created_at desc']);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    per_page: 15,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  // Views and tabs state
  const [views, setViews] = useState([...defaultImportJobViews]);
  const [selectedView, setSelectedView] = useState(0);
  const [activePopover, setActivePopover] = useState<number | null>(null);

  const { mode, setMode } = useSetIndexFiltersMode();

  const resourceName = {
    singular: 'import job',
    plural: 'import jobs',
  };

  // Apply view filters
  const applyViewFilters = useCallback((view: any) => {
    if (view && view.filters) {
      // Apply view-specific filters
      if (view.filters.status) {
        setStatusFilter([view.filters.status]);
      } else {
        setStatusFilter([]);
      }
    } else {
      // Reset filters
      setStatusFilter([]);
    }
  }, []);

  useEffect(() => {
    applyViewFilters(views[selectedView]);
  }, [selectedView, views, applyViewFilters]);

  const fetchImportJobs = useCallback(async (page = 1, query = '', filters: Filters = {}, sort = sortSelected[0]) => {
    setError(null);
    setLoading(true);
    
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: '15'
    });
    
    if (query) {
      params.append('query', query);
    }

    // Add sorting
    if (sort) {
      params.append('sort', sort);
    }

    // Add filters
    if (filters.status && filters.status.length > 0) {
      filters.status.forEach(status => {
        params.append('status[]', status);
      });
    }

    const result = await withApiErrorHandling(
      () => axios.get(`/import_jobs?${params}`)
    ) as any;
    
    if (result && result.success) {
      setImportJobs(result.data.import_jobs);
      setPagination(result.data.pagination);
    } else if (result && !result.success) {
      setError(result.error);
    } else {
      setError('Failed to fetch import jobs');
    }
    
    setLoading(false);
  }, [sortSelected]);

  useEffect(() => {
    const filters = {
      status: statusFilter,
    };
    fetchImportJobs(pagination.page, queryValue, filters, sortSelected[0]);
  }, [fetchImportJobs, pagination.page, queryValue, statusFilter, sortSelected]);

  const handleFiltersQueryChange = useCallback(
    (value: string) => {
      setQueryValue(value);
      setPagination(prev => ({ ...prev, page: 1 }));
    },
    []
  );

  const handleFiltersClearAll = useCallback(() => {
    setQueryValue('');
    setStatusFilter([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    // Apply current view filters after clearing
    applyViewFilters(views[selectedView]);
  }, [applyViewFilters, selectedView, views]);

  const handleSortChange = useCallback((value: string[]) => {
    setSortSelected(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleDownloadOutputFile = useCallback(async (jobId: number) => {
    try {
      const response = await axios.get(`/import_jobs/${jobId}/download_error_file`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `import_job_${jobId}_output.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download output file:', error);
      // You could add a toast notification here
    }
  }, []);





  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: 'success' | 'critical' | 'warning' | 'info' } = {
      pending: 'info',
      processing: 'warning',
      completed: 'success',
      failed: 'critical',
      cancelled: 'info',
    };
    
    const humanizedStatus = status
      .replace('_', ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return getColoredBadge(humanizedStatus, statusColors[status] || 'info');
  };

  const getColoredBadge = (text: string, color: 'success' | 'critical' | 'warning' | 'info') => {
    const colorMap = {
      success: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
      critical: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
      warning: { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' },
      info: { bg: '#d1ecf1', text: '#0c5460', border: '#bee5eb' }
    };

    const colors = colorMap[color];

    return (
      <span style={{
        backgroundColor: colors.bg,
        color: colors.text,
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        border: `1px solid ${colors.border}`,
        display: 'inline-block',
        marginRight: '4px'
      }}>
        {text}
      </span>
    );
  };

  const getGrayBadge = (text: string) => {
    return (
      <span style={{
        backgroundColor: '#f6f6f7',
        color: '#202223',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        border: '1px solid #e1e3e5',
        display: 'inline-block',
        marginRight: '4px'
      }}>
        {text}
      </span>
    );
  };

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Pending', value: 'pending' },
            { label: 'Processing', value: 'processing' },
            { label: 'Completed', value: 'completed' },
            { label: 'Failed', value: 'failed' },
            { label: 'Cancelled', value: 'cancelled' },
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple={true}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [
    ...(statusFilter.length > 0 ? [{ key: 'status', label: `Status: ${statusFilter.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`, onRemove: () => setStatusFilter([]) }] : []),
  ];

  // Build tabs from views
  const tabs = views.map((view, index) => ({
    id: view.id,
    content: view.name,
    accessibilityLabel: view.name,
    panelID: `${view.id}-import-jobs`,
  }));

  const rowMarkup = importJobs.map(
    ({ id, status, progress, total_records, processed_records, error_count, success_count, created_count, updated_count, created_at, duration_formatted, success_rate, error_rate, error_file }, index) => {
      const date = new Date(created_at);
      const dateFormatted = date.toLocaleDateString();
      const timeFormatted = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const actions = [];
      
      // Add download output file action if output file exists and job is completed or failed
      if (error_file && (status === 'completed' || status === 'failed')) {
        actions.push({
          content: 'Download Output File',
          onAction: () => handleDownloadOutputFile(id),
        });
      }

      return (
        <IndexTable.Row
          id={id.toString()}
          key={id}
          position={index}
        >
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                Import Job #{id}
              </Text>
              <InlineStack gap="200">
                {getStatusBadge(status)}
                {getGrayBadge(`${total_records} records`)}
              </InlineStack>
              {status === 'processing' && (
                <ProgressBar progress={progress} size="small" />
              )}
            </BlockStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                {processed_records} / {total_records}
              </Text>
              <Text variant="bodySm" as="span" tone="subdued">
                {success_count} successful, {error_count} errors
              </Text>
              {created_count > 0 && (
                <Text variant="bodySm" as="span" tone="subdued">
                  Created: {created_count}, Updated: {updated_count}
                </Text>
              )}
              {success_rate > 0 && (
                <Text variant="bodySm" as="span" tone="subdued">
                  Success rate: {success_rate}%
                </Text>
              )}
            </BlockStack>
          </IndexTable.Cell>
                      <IndexTable.Cell>
              <BlockStack gap="100">
                <Text variant="bodyMd" as="span">
                  {dateFormatted}
                </Text>
                <Text variant="bodySm" as="span" tone="subdued">
                  {timeFormatted}
                </Text>
                {duration_formatted !== 'N/A' && (
                  <Text variant="bodySm" as="span" tone="subdued">
                    Duration: {duration_formatted}
                  </Text>
                )}
              </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              {actions.length > 0 && (
                <Popover
                  active={activePopover === id}
                  activator={
                    <Button
                      variant="tertiary"
                      size="slim"
                      icon={MenuHorizontalIcon}
                      onClick={() => setActivePopover(activePopover === id ? null : id)}
                    />
                  }
                  onClose={() => setActivePopover(null)}
                >
                  <ActionList
                    actionRole="menuitem"
                    items={actions}
                  />
                </Popover>
              )}
            </IndexTable.Cell>
        </IndexTable.Row>
      );
    },
  );

  if (loading && importJobs.length === 0) {
    return (
      <Page title="Import Jobs" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p">Loading import jobs...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error && importJobs.length === 0) {
    return (
      <Page title="Import Jobs" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Import Jobs" fullWidth>
      <Layout>
        <Layout.Section>
          <Card>
            <IndexFilters
              queryValue={queryValue}
              queryPlaceholder="Search import jobs by status or error message"
              onQueryChange={handleFiltersQueryChange}
              onQueryClear={() => setQueryValue('')}
              cancelAction={{
                onAction: handleFiltersClearAll,
                disabled: false,
                loading: false,
              }}
              tabs={tabs}
              selected={selectedView}
              onSelect={(selectedTabIndex) => {
                setSelectedView(selectedTabIndex);

                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              filters={filters}
              appliedFilters={appliedFilters}
              onClearAll={handleFiltersClearAll}
              mode={mode}
              setMode={setMode}
              sortOptions={importJobSortOptions}
              sortSelected={sortSelected}
              onSort={handleSortChange}
              primaryAction={undefined}
              canCreateNewView={false}
            />
            <div style={{ maxHeight: '75vh', overflow: 'auto' }}>
            <IndexTable
              resourceName={resourceName}
              itemCount={importJobs.length}
              selectable={false}
              headings={[
                { title: 'Job & Status' },
                { title: 'Progress & Results' },
                { title: 'Date & Duration' },
                { title: 'Actions' },
              ]}
              emptyState={
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    {queryValue || appliedFilters.length > 0
                      ? 'No import jobs found matching your search criteria.' 
                      : 'No import jobs found. Import jobs will appear here when you upload files.'}
                  </Text>
                </div>
              }
            >
              {rowMarkup}
            </IndexTable>
            </div>
            
            {pagination.total_pages > 1 && (
              <div style={{ background: '#fff', borderTop: '1px solid #e1e3e5', padding: '12px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  hasPrevious={pagination.has_prev}
                  onPrevious={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  hasNext={pagination.has_next}
                  onNext={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  label={`Page ${pagination.page} of ${pagination.total_pages}`}
                />
              </div>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default ImportJobs;
